import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, AlertCircle, Eye, Calendar, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { withBackend } from '../utils/api';

const SharedContentView = () => {
  const pathMatch = window.location.pathname.match(/^\/share\/(.+)/);
  const hash = pathMatch?.[1];

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);
  const slideContainerRef = useRef(null);
  const [slideScale, setSlideScale] = useState(null);

  const updateSlideScale = useCallback(() => {
    if (slideContainerRef.current) {
      const w = slideContainerRef.current.offsetWidth;
      setSlideScale(w / 1280);
    }
  }, []);

  useEffect(() => {
    updateSlideScale();
    window.addEventListener('resize', updateSlideScale);
    return () => window.removeEventListener('resize', updateSlideScale);
  }, [updateSlideScale]);

  useEffect(() => {
    // Recalculate when slide changes or report loads (double timeout: fast + safety net)
    const t1 = setTimeout(updateSlideScale, 50);
    const t2 = setTimeout(updateSlideScale, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentSlide, report, updateSlideScale]);

  useEffect(() => {
    if (hash) fetchContent();
  }, [hash]);

  const fetchContent = async () => {
    try {
      const response = await fetch(withBackend(`/api/reports/${hash}`));
      if (!response.ok) {
        throw new Error(response.status === 404 ? 'Content not found or has expired' : 'Failed to load content');
      }
      const data = await response.json();
      if (data.success) {
        setReport(data.report);
      } else {
        throw new Error(data.error || 'Failed to load content');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading shared content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Not Found</h1>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <a href="/" className="inline-block px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Go to wizardly.ai
          </a>
        </div>
      </div>
    );
  }

  const contentType = report.content_type || 'analysis';
  const title = report.presentation_name || 'Shared Content';

  // Route to the right viewer
  if (contentType === 'analysis') {
    // Redirect to legacy analysis viewer
    window.location.href = `/report/${hash}`;
    return null;
  }

  // HTML-based content (dashboard, document, infographic, sheets)
  if (['dashboard', 'document', 'infographic', 'sheets'].includes(contentType)) {
    // Documents are often generated as multiple stacked sections (each ~720px tall) that
    // exceed the iframe viewport. Without an explicit overflow override, the slide-canvas
    // rules (body { overflow: hidden }) inherited at generation time prevent scrolling
    // and only the first section is visible. Inject a scroll-enabling style block.
    const docHtml = (() => {
      const raw = report.html_content || '';
      if (!raw) return '';
      const override = '<style id="shared-doc-scroll-override">html,body{overflow:auto!important;height:auto!important;min-height:100%!important;}body>*{max-height:none!important;}</style>';
      if (raw.includes('</head>')) return raw.replace('</head>', override + '</head>');
      if (raw.includes('<body')) return raw.replace(/<body([^>]*)>/i, `<body$1>${override}`);
      return override + raw;
    })();

    return (
      <div className={`min-h-screen bg-slate-950 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        {/* Header bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-900 border-b border-slate-800">
          <a href="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
            wizardly.ai
          </a>
          <span className="text-slate-700">/</span>
          <h1 className="text-sm font-medium text-white truncate flex-1">{title}</h1>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> {report.view_count} views
            </span>
            {report.expires_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Expires {formatDate(report.expires_at)}
              </span>
            )}
            <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-400 capitalize">{contentType}</span>
            <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-1 hover:bg-slate-800 rounded transition-colors">
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* HTML content in iframe */}
        <div className="flex-1">
          <iframe
            ref={iframeRef}
            srcDoc={docHtml}
            title={title}
            className="w-full h-full border-0"
            style={{ minHeight: isFullscreen ? '100vh' : 'calc(100vh - 45px)' }}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>
    );
  }

  // Presentation slides
  if (contentType === 'presentation') {
    const slides = report.slides_data || [];
    const slide = slides[currentSlide];

    return (
      <div className={`min-h-screen bg-slate-950 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        {/* Header bar */}
        {!isFullscreen && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-900 border-b border-slate-800">
            <a href="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
              wizardly.ai
            </a>
            <span className="text-slate-700">/</span>
            <h1 className="text-sm font-medium text-white truncate flex-1">{title}</h1>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> {report.view_count} views
              </span>
              {report.expires_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Expires {formatDate(report.expires_at)}
                </span>
              )}
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-400">
                Slide {currentSlide + 1} of {slides.length}
              </span>
              <button onClick={() => setIsFullscreen(true)} className="p-1 hover:bg-slate-800 rounded transition-colors">
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Slide viewer */}
        <div className="flex-1 flex items-center justify-center relative" onClick={() => isFullscreen && setIsFullscreen(false)}>
          {slide?.html_source ? (
            <div className="w-full max-w-5xl mx-auto px-8 py-6">
              {/* Scale-to-fit container: inner iframe is always 1280×720, scaled down to fit */}
              <div
                ref={slideContainerRef}
                className="relative w-full rounded-lg overflow-hidden shadow-2xl bg-white"
                style={{ paddingBottom: `${(720 / 1280) * 100}%` }}
              >
                {slideScale !== null && (
                  <iframe
                    srcDoc={slide.html_source}
                    title={`Slide ${currentSlide + 1}`}
                    className="absolute top-0 left-0 border-0"
                    style={{
                      width: '1280px',
                      height: '720px',
                      transformOrigin: 'top left',
                      transform: `scale(${slideScale})`,
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    onLoad={(e) => {
                      try {
                        const doc = e.target.contentDocument;
                        if (!doc) return;
                        const h = doc.body.scrollHeight;
                        if (h > 720) {
                          const fit = 720 / h;
                          doc.body.style.transform = `scale(${fit})`;
                          doc.body.style.transformOrigin = 'top left';
                          doc.body.style.width = `${1280 / fit}px`;
                          doc.body.style.overflow = 'hidden';
                        }
                      } catch (_) {}
                    }}
                  />
                )}
              </div>
            </div>
          ) : slide?.preview_url ? (
            <div className="w-full max-w-5xl mx-auto px-8 py-6">
              <img src={slide.preview_url} alt={`Slide ${currentSlide + 1}`} className="w-full rounded-lg shadow-2xl" />
            </div>
          ) : (
            <p className="text-slate-500">No content for this slide</p>
          )}

          {/* Navigation arrows */}
          {slides.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentSlide(Math.max(0, currentSlide - 1)); }}
                disabled={currentSlide === 0}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-full transition-colors disabled:opacity-30"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1)); }}
                disabled={currentSlide === slides.length - 1}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-full transition-colors disabled:opacity-30"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Fullscreen slide counter */}
          {isFullscreen && slides.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-800/80 rounded-full text-xs text-slate-300">
              {currentSlide + 1} / {slides.length}
            </div>
          )}
        </div>

        {/* Slide thumbnails strip */}
        {!isFullscreen && slides.length > 1 && (
          <div className="px-4 py-3 bg-slate-900 border-t border-slate-800 flex gap-2 overflow-x-auto">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`flex-shrink-0 w-24 aspect-[16/9] rounded border-2 overflow-hidden transition-colors ${
                  i === currentSlide ? 'border-blue-500' : 'border-slate-700 hover:border-slate-500'
                }`}
              >
                {s.preview_url ? (
                  <img src={s.preview_url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500 text-xs">{i + 1}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <p className="text-slate-400">Unsupported content type: {contentType}</p>
    </div>
  );
};

export default SharedContentView;
